// Battle Royale zone (architecture §9.2 step 8, ui-flow §6.3/§6.8; M3 data): the current circle
// as a thick line with a red tint outside it, and the next circle dashed. Drawn from the MATCH
// section's zone in the world transform.

import type { ZoneInfo } from '@ballio/shared';
import { type Frame, useWorldTransform } from './frame.js';
import { traceCircle } from './outline.js';
import { css, cssAlpha } from './theme.js';

/** Outside-the-zone tint (a warm red) and line widths in CSS px. */
export const ZONE_STYLE = Object.freeze({
  tint: 0xe5484d,
  tintAlpha: 0.14,
  lineCssPx: 6,
  nextLineCssPx: 3,
  dashCssPx: [18, 12] as const,
});

export function drawZone(f: Frame, zone: ZoneInfo): void {
  const { ctx, view, zoom } = f;
  useWorldTransform(f);
  ctx.globalAlpha = 1;
  const r = Math.max(0, zone.r);
  // Red tint over everything outside the current circle (even-odd: rectangle minus circle).
  ctx.beginPath();
  ctx.rect(view.minX, view.minY, view.maxX - view.minX, view.maxY - view.minY);
  if (r > 0) traceCircle(ctx, zone.cx, zone.cy, r);
  ctx.fillStyle = cssAlpha(ZONE_STYLE.tint, ZONE_STYLE.tintAlpha);
  ctx.fill('evenodd');
  const line = css(f.theme.colors.border);
  if (r > 0) {
    ctx.beginPath();
    traceCircle(ctx, zone.cx, zone.cy, r);
    ctx.lineWidth = ZONE_STYLE.lineCssPx / zoom;
    ctx.strokeStyle = line;
    ctx.stroke();
  }
  if (zone.nextR > 0 && zone.nextR < r) {
    ctx.beginPath();
    traceCircle(ctx, zone.nextCx, zone.nextCy, zone.nextR);
    ctx.setLineDash([ZONE_STYLE.dashCssPx[0] / zoom, ZONE_STYLE.dashCssPx[1] / zoom]);
    ctx.lineWidth = ZONE_STYLE.nextLineCssPx / zoom;
    ctx.strokeStyle = line;
    ctx.stroke();
    ctx.setLineDash([]);
  }
}
